import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient, formatSAR } from "../lib/api";
import { sameConvList, sameMessageList, useSmartAutoScroll } from "../lib/chatUtils";
import { MessageSquare, Send, Loader2, ArrowLeft, Search, Star, Archive, Trash2, MoreVertical, Inbox, CheckCheck } from "lucide-react";

function timeAgo(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return lang === "ar" ? `${Math.floor(diff)} ث` : `${Math.floor(diff)}s`;
  if (diff < 3600) return lang === "ar" ? `${Math.floor(diff / 60)} د` : `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return lang === "ar" ? `${Math.floor(diff / 3600)} س` : `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US");
}

function initialsOf(name) {
  const s = (name || "?").trim();
  return s.charAt(0).toUpperCase();
}

// Hide technical role identifiers from the UI. Anything not mapped is
// rendered as-is only when it's already a human-readable label; unknown
// technical values (uppercase snake_case) return null so the caller can
// skip rendering the badge.
function friendlyRole(role, lang = "ar") {
  if (!role) return null;
  const key = String(role).toLowerCase();
  const MAP = {
    super_admin: { ar: "إدارة CARTAL", en: "CARTAL Admin" },
    admin:       { ar: "إدارة CARTAL", en: "CARTAL Admin" },
    dealer:      { ar: "معرض سيارات", en: "Dealer" },
    business:    { ar: "نشاط تجاري",   en: "Business" },
    seller:      { ar: "بائع",         en: "Seller" },
    buyer:       { ar: "مشتري",        en: "Buyer" },
    individual_buyer: { ar: "مستخدم", en: "User" },
    individual_seller: { ar: "بائع فردي", en: "Seller" },
    business_member: { ar: "عضو منشأة", en: "Business member" },
  };
  const hit = MAP[key];
  if (hit) return lang === "ar" ? hit.ar : hit.en;
  // If it looks like a technical constant (all upper + underscores) hide it.
  if (/^[A-Z0-9_]+$/.test(String(role))) return null;
  return role;
}

function ConversationListItemInner({ conv, active, onActivate, onStar, onArchive, onDelete }) {
  const { lang } = useI18n();
  const peerName = conv.peer?.company_name || conv.peer?.name || (lang === "ar" ? "مستخدم" : "User");
  const peerRole = friendlyRole(conv.peer?.role, lang);
  const unread = conv.my_unread || 0;
  const hasUnread = unread > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div
      className={`group relative w-full p-3 rounded-xl transition-colors border ${active ? "bg-[#c9a55c]/12 border-[#c9a55c]/50" : hasUnread ? "border-[#c9a55c]/25 bg-[#fdf9ef]" : "border-transparent hover:bg-black/[0.03]"}`}
      data-testid={`conv-item-${conv.id}`}
    >
      <button
        type="button"
        onClick={() => onActivate(conv)}
        className="w-full text-start flex items-start gap-3"
      >
        {/* Avatar */}
        <div className="relative shrink-0">
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-[15px] ${hasUnread ? "bg-[#c9a55c] text-white" : "bg-[#0a0a0a]/[0.06] text-[#525252]"}`}
          >
            {initialsOf(peerName)}
          </div>
          {hasUnread && (
            <span
              className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid={`conv-${conv.id}-unread`}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className={`font-display text-[15px] truncate ${hasUnread ? "font-bold text-[#0a0a0a]" : "text-[#0a0a0a]"}`}>
              {peerName}
              {conv.is_starred && <Star className="inline w-3 h-3 text-[#c9a55c] fill-[#c9a55c] ms-1.5 -translate-y-0.5" />}
            </div>
            <div className="text-[10px] text-[#8a8a8a] shrink-0">{timeAgo(conv.last_message_at, lang)}</div>
          </div>
          {peerRole && (
            <div className="text-[10px] uppercase tracking-widest text-[#8a6f2e] font-semibold mt-0.5">{peerRole}</div>
          )}
          {conv.vehicle_snapshot && (
            <div className="text-[11px] text-[#8a6f2e] mt-0.5 truncate">
              {conv.vehicle_snapshot.year} {conv.vehicle_snapshot.make} {conv.vehicle_snapshot.model}
            </div>
          )}
          <div className={`text-[12px] mt-1 truncate ${hasUnread ? "text-[#0a0a0a] font-medium" : "text-[#525252]"}`}>
            {conv.last_message || (lang === "ar" ? "محادثة جديدة" : "New conversation")}
          </div>
        </div>
      </button>

      {/* Actions menu */}
      <div className="absolute top-2 end-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          className="w-7 h-7 rounded-full bg-white/95 border border-black/[0.08] flex items-center justify-center hover:bg-[#f0eee7]"
          data-testid={`conv-${conv.id}-menu`}
        >
          <MoreVertical className="w-3.5 h-3.5 text-[#525252]" />
        </button>
        {menuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
            <div className="absolute end-0 mt-1 w-40 rounded-lg bg-white border border-black/[0.08] shadow-lg z-50 py-1">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onStar(conv); }}
                className="w-full text-start px-3 py-2 text-[12px] hover:bg-[#f0eee7] flex items-center gap-2"
                data-testid={`conv-${conv.id}-star`}
              >
                <Star className={`w-3.5 h-3.5 ${conv.is_starred ? "fill-[#c9a55c] text-[#c9a55c]" : "text-[#525252]"}`} />
                {conv.is_starred ? (lang === "ar" ? "إلغاء الحفظ" : "Unstar") : (lang === "ar" ? "حفظ" : "Star")}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onArchive(conv); }}
                className="w-full text-start px-3 py-2 text-[12px] hover:bg-[#f0eee7] flex items-center gap-2"
                data-testid={`conv-${conv.id}-archive`}
              >
                <Archive className="w-3.5 h-3.5 text-[#525252]" />
                {conv.is_archived ? (lang === "ar" ? "إلغاء الأرشفة" : "Unarchive") : (lang === "ar" ? "أرشفة" : "Archive")}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(conv); }}
                className="w-full text-start px-3 py-2 text-[12px] hover:bg-red-50 text-red-700 flex items-center gap-2"
                data-testid={`conv-${conv.id}-delete`}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {lang === "ar" ? "حذف" : "Delete"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
// iter105 — memoise conversation items so the 8-second sidebar poll only
// re-renders items whose data actually changed. Prevents the "dancing"
// list where every card twitched every refresh.
const ConversationListItem = React.memo(ConversationListItemInner, (a, b) =>
  a.active === b.active &&
  a.onActivate === b.onActivate &&
  a.onStar === b.onStar &&
  a.onArchive === b.onArchive &&
  a.onDelete === b.onDelete &&
  a.conv.id === b.conv.id &&
  a.conv.last_message === b.conv.last_message &&
  a.conv.last_message_at === b.conv.last_message_at &&
  (a.conv.my_unread || 0) === (b.conv.my_unread || 0) &&
  !!a.conv.is_starred === !!b.conv.is_starred &&
  !!a.conv.is_archived === !!b.conv.is_archived
);

function MessageBubble({ msg, mine }) {
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          mine ? "bg-cyan-400 text-[#001a23]" : "glass border-white/10"
        }`}
        data-testid={`msg-${msg.id}`}
      >
        {msg.body}
        <div className={`text-[10px] mt-1 ${mine ? "text-[#001a23]/60" : "text-[#6b6b6b]"}`}>
          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}
// iter105 — memoise so polling refresh doesn't re-render every bubble.
const MemoBubble = React.memo(MessageBubble, (a, b) =>
  a.mine === b.mine && a.msg.id === b.msg.id && a.msg.body === b.msg.body
);

export default function MessagesPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState("");

  // Viewport-anchored positioning: the surrounding app shell (Header,
  // Breadcrumbs, Footer, mobile banner) is NOT a fixed pixel count. We
  // measure the actual top offset of a zero-height sentinel placed at
  // the natural position inside the route, then pin the messaging pane
  // to that Y-coordinate with position:fixed inset:0. This removes the
  // hardcoded 72px assumption without touching the shared Layout.
  const anchorRef = useRef(null);
  const [topOffset, setTopOffset] = useState(140);
  useLayoutEffect(() => {
    const measure = () => {
      if (!anchorRef.current) return;
      const t = anchorRef.current.getBoundingClientRect().top + window.scrollY;
      setTopOffset(Math.max(0, Math.round(t)));
    };
    measure();
    window.addEventListener("resize", measure);
    // Landscape orientation change on mobile: the URL bar collapses and
    // the sentinel's Y position shifts. Without this listener the fixed
    // pane keeps its previous height and appears "frozen" until reload.
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  // Lock body scroll while the messaging page is mounted so the document
  // never competes with the two inner scrollers (sidebar / thread). The
  // previous overflow value is restored on navigation, so unrelated pages
  // are unaffected.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    // Also collapse the shared Footer while /messages is open so the
    // document's scrollable content matches the viewport height (the
    // messaging pane is position:fixed and owns the visible area). The
    // <style> element is scoped by the .cartal-msgs-open body class and
    // fully removed on unmount, so no other page is affected.
    document.body.classList.add("cartal-msgs-open");
    const style = document.createElement("style");
    style.setAttribute("data-cartal-msgs-lock", "1");
    // The AI Assistant fab/panel and the mobile app-install banner are both
    // globally-mounted `position: fixed` elements (Layout.jsx) that sit on
    // top of any route, including this one. On mobile they overlap the
    // composer/Send button, and in landscape they crush the little vertical
    // space this page has left. Hide both while the messaging pane owns the
    // viewport; both are restored on unmount, same as the footer below.
    style.textContent = `
      body.cartal-msgs-open [data-testid="site-footer"] { display: none !important; }
      body.cartal-msgs-open [data-testid="ai-help-fab"] { display: none !important; }
      body.cartal-msgs-open [data-testid="ai-help-panel"] { display: none !important; }
      body.cartal-msgs-open [data-testid="mobile-app-banner"] { display: none !important; }
      /* Landscape mobile fit: at very short viewports (e.g. 812x375), the
         decorative page heading eats ~80px which pushes the composer below
         the fold. Collapse it so the thread + form always stay visible. */
      @media (max-height: 520px) {
        body.cartal-msgs-open [data-testid="messages-page-heading"] { display: none !important; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
      document.body.classList.remove("cartal-msgs-open");
      style.remove();
    };
  }, []);

  // `active` is read through a ref inside loadConvs/the polling effect below
  // instead of being a dependency. Previously loadConvs was recreated on
  // every `active`/`params` change, which tore down and recreated BOTH
  // polling intervals (5s + 8s) below on every cycle, firing an immediate
  // off-cadence refetch each time — that off-cadence refetch is what
  // produced the message rows visibly shifting position ("dancing"),
  // independent of the earlier document-scroll fix.
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  const loadConvs = useCallback(async () => {
    const { data } = await apiClient.get("/conversations");
    const next = data.items || [];
    setConvs((prev) => (sameConvList(prev, next) ? prev : next));
    const target = new URLSearchParams(window.location.search).get("c");
    const curActive = activeRef.current;
    // Only call setActive if the ACTIVE conversation identity actually
    // changed (previously the 8s poll passed a fresh object reference
    // every time, remounting the entire message pane — "dancing").
    if (target && (!curActive || curActive.id !== target)) {
      const found = next.find((c) => c.id === target);
      if (found && (!curActive || curActive.id !== found.id)) setActive(found);
    } else if (!curActive && next.length && window.innerWidth >= 1024) {
      // Auto-select the first conversation ONLY on desktop. On mobile
      // we intentionally leave `active` null so the user sees the
      // conversation LIST first (proper mobile mail-app UX) and taps a
      // conversation to open its full-screen view.
      setActive(next[0]);
    }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    const { data } = await apiClient.get(`/conversations/${convId}/messages`);
    const next = data.items || [];
    setMessages((prev) => (sameMessageList(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    if (!user) return;
    loadConvs();
  }, [user, loadConvs]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    setParams({ c: active.id }, { replace: true });
    const t = setInterval(() => loadMessages(active.id), 5000);
    const t2 = setInterval(loadConvs, 8000);
    // iter233 — refetch immediately when the tab regains focus so a
    // user returning from another tab / app doesn't have to wait up
    // to 5s to see messages that arrived while they were away.
    const onVis = () => {
      if (document.visibilityState === "visible") {
        loadMessages(active.id);
        loadConvs();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      clearInterval(t);
      clearInterval(t2);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
    // Deliberately keyed on active.id only. loadMessages/loadConvs/setParams
    // are referentially stable (empty-dep useCallback / react-router's
    // stable setter) — including them here was what caused the interval
    // teardown/recreate loop on every poll cycle.
  }, [active?.id]);

  // Stable callback so the memoised ConversationListItem doesn't re-render
  // just because the parent produced a fresh arrow function (iter105).
  const handleActivate = useCallback((c) => setActive(c), []);

  const doStar = useCallback(async (c) => {
    try { await apiClient.patch(`/conversations/${c.id}/star`); await loadConvs(); }
    catch { toast.error(lang === "ar" ? "تعذّر التحديث" : "Failed"); }
  }, [loadConvs, lang]);

  const doArchive = useCallback(async (c) => {
    try {
      await apiClient.patch(`/conversations/${c.id}/archive`);
      toast.success(lang === "ar" ? (c.is_archived ? "تم إلغاء الأرشفة" : "تمت الأرشفة") : (c.is_archived ? "Unarchived" : "Archived"));
      await loadConvs();
    } catch { toast.error(lang === "ar" ? "تعذّر التحديث" : "Failed"); }
  }, [loadConvs, lang]);

  const doDelete = useCallback(async (c) => {
    if (!window.confirm(lang === "ar" ? `حذف محادثة "${c.peer?.company_name || c.peer?.name || ""}"؟` : "Delete this conversation?")) return;
    try {
      await apiClient.delete(`/conversations/${c.id}`);
      toast.success(lang === "ar" ? "تم الحذف" : "Deleted");
      if (active?.id === c.id) setActive(null);
      await loadConvs();
    } catch { toast.error(lang === "ar" ? "تعذّر الحذف" : "Failed"); }
  }, [loadConvs, lang, active]);

  const [tab, setTab] = useState("inbox"); // inbox | unread | starred | archived

  // Smart auto-scroll shared across the platform (chatUtils). Prevents the
  // "dancing messages" bug: only jumps to bottom when the user is already
  // near it, or has just sent a message themselves.
  const threadRef = useSmartAutoScroll(messages, user?.id);

  // Derived state — MUST live above the early-return so hook order is
  // stable across renders (React rules-of-hooks).
  const filteredConvs = useMemo(() => {
    let list = convs;
    // iter233 — keep the currently-open conversation visible in tabs
    // that filter by "unread"/"starred", otherwise it visibly disappears
    // from the sidebar the instant the user opens it (unread → 0), or
    // the moment they unstar it. Root cause of "الرسائل تختفي أحيانًا":
    // list_messages on the backend auto-clears unread, so the very next
    // poll drops the row from a strict `my_unread > 0` filter.
    if (tab === "unread") list = list.filter((c) => (
      ((c.my_unread || 0) > 0 || (active && c.id === active.id)) && !c.is_archived
    ));
    else if (tab === "starred") list = list.filter((c) => (
      (c.is_starred || (active && c.id === active.id)) && !c.is_archived
    ));
    else if (tab === "archived") list = list.filter((c) => c.is_archived);
    else list = list.filter((c) => !c.is_archived);
    if (filter) {
      const f = filter.toLowerCase();
      list = list.filter((c) => {
        const name = (c.peer?.company_name || c.peer?.name || "").toLowerCase();
        const vm = c.vehicle_snapshot ? `${c.vehicle_snapshot.make} ${c.vehicle_snapshot.model}`.toLowerCase() : "";
        return name.includes(f) || vm.includes(f);
      });
    }
    return list;
  }, [convs, tab, filter, active]);

  const totalUnread = useMemo(
    () => convs.reduce((s, c) => s + (c.is_archived ? 0 : (c.my_unread || 0)), 0),
    [convs],
  );

  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/messages" }} replace />;

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      await apiClient.post(`/conversations/${active.id}/messages`, { body });
      setBody("");
      await loadMessages(active.id);
      await loadConvs();
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Zero-height sentinel — measures the natural top offset that
          Header + Breadcrumbs occupy on this route. See useLayoutEffect. */}
      <div ref={anchorRef} aria-hidden style={{ height: 0 }} />
      <div
        style={{ position: "fixed", top: topOffset, insetInline: 0, bottom: 0, zIndex: 5 }}
        className="flex flex-col overflow-hidden"
        data-testid="messages-page"
      >
        {/* pb-20 was reserving mobile bottom space to clear the AI Assistant fab / app banner.
            Both are now hidden on this route (see cartal-msgs-open style above), so a flat
            pb-4 is enough at any width, including landscape where vertical space is tight. */}
        <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-10 pt-4 pb-4 h-full flex flex-col overflow-hidden">
          <div className="mb-4 flex-shrink-0" data-testid="messages-page-heading">
            <div className="overline mb-2 inline-flex items-center gap-2"><MessageSquare className="w-3 h-3" /> MESSAGES</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="font-display text-3xl tracking-tight font-medium">
                {lang === "ar" ? "الرسائل" : "Messages"}
              </h1>
              {totalUnread > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[11px] font-bold"
                  data-testid="total-unread-badge"
                >
                  {totalUnread} {lang === "ar" ? "غير مقروءة" : "unread"}
                </span>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-[340px_1fr] gap-4 lg:gap-6 flex-1 min-h-0">
        <aside className={`surface rounded-2xl p-3 lg:p-4 flex-col min-h-0 ${active ? "hidden lg:flex" : "flex"}`}>
          {/* Filter tabs — Inbox / Unread / Starred / Archived */}
          <div className="flex gap-1 mb-2 rounded-lg bg-[#0a0a0a]/[0.04] p-1" data-testid="conv-tabs">
            {[
              { key: "inbox",    Icon: Inbox,     labelAr: "الوارد",       labelEn: "Inbox" },
              { key: "unread",   Icon: CheckCheck,labelAr: "غير مقروءة",   labelEn: "Unread" },
              { key: "starred",  Icon: Star,      labelAr: "محفوظة",      labelEn: "Starred" },
              { key: "archived", Icon: Archive,   labelAr: "أرشيف",       labelEn: "Archive" },
            ].map(({ key, Icon, labelAr, labelEn }) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex-1 px-1.5 py-1.5 rounded-md text-[11px] font-semibold flex items-center justify-center gap-1 transition ${tab === key ? "bg-white text-[#0a0a0a] shadow-sm" : "text-[#525252] hover:text-[#0a0a0a]"}`}
                data-testid={`tab-${key}`}
              >
                <Icon className="w-3 h-3" />
                <span className="hidden sm:inline">{lang === "ar" ? labelAr : labelEn}</span>
              </button>
            ))}
          </div>
          <div className="relative mb-2">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b6b6b]" />
            <input className="input-dark ps-10 text-sm" placeholder={lang === "ar" ? "ابحث في المحادثات…" : "Search conversations…"} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="conv-search" />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 mt-2 min-h-0">
            {filteredConvs.length === 0 ? (
              <div className="text-center text-[#6b6b6b] py-12 text-sm">
                {tab === "unread"   ? (lang === "ar" ? "لا توجد رسائل غير مقروءة." : "No unread messages.")
                : tab === "starred" ? (lang === "ar" ? "لا توجد محادثات محفوظة." : "No starred conversations.")
                : tab === "archived"? (lang === "ar" ? "الأرشيف فارغ." : "Archive is empty.")
                :                     (lang === "ar" ? "لا توجد محادثات بعد." : "No conversations yet.")}
              </div>
            ) : filteredConvs.map((c) => (
              <ConversationListItem
                key={c.id}
                conv={c}
                active={active?.id === c.id}
                onActivate={handleActivate}
                onStar={doStar}
                onArchive={doArchive}
                onDelete={doDelete}
              />
            ))}
          </div>
        </aside>

        <section
          className={`surface rounded-2xl flex-col min-h-0 ${active ? "flex" : "hidden lg:flex"}`}
          data-testid="messages-section"
        >
          {!active ? (
            // Empty state — desktop only (mobile hides the whole section
            // when there's no active conversation so the list is the
            // visible view).
            <div className="flex-1 flex flex-col items-center justify-center text-[#6b6b6b] p-8 text-center">
              <MessageSquare className="w-10 h-10 text-[#c9a55c] mb-3" />
              <p>{lang === "ar" ? "اختر محادثة من القائمة، أو ابدأ واحدة من صفحة سيارة." : "Pick a conversation from the list, or start one from a vehicle page."}</p>
            </div>
          ) : (
            <>
              <header className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] flex items-center justify-between gap-2 sm:gap-3 bg-white/60">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setActive(null)}
                    className="lg:hidden w-8 h-8 rounded-full bg-[#0a0a0a]/[0.04] hover:bg-[#0a0a0a]/[0.08] flex items-center justify-center text-[#0a0a0a] shrink-0"
                    data-testid="conv-back"
                    aria-label={lang === "ar" ? "رجوع" : "Back"}
                  >
                    <ArrowLeft className="w-4 h-4" style={{ transform: lang === "ar" ? "scaleX(-1)" : "none" }} />
                  </button>
                  <div className="w-9 h-9 rounded-full bg-[#c9a55c] text-white font-bold flex items-center justify-center shrink-0">
                    {initialsOf(active.peer?.company_name || active.peer?.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[14px] sm:text-[15px] truncate text-[#0a0a0a]" data-testid="active-peer-name">
                      {active.peer?.company_name || active.peer?.name || (lang === "ar" ? "مستخدم" : "User")}
                      {active.is_starred && <Star className="inline w-3 h-3 text-[#c9a55c] fill-[#c9a55c] ms-1.5 -translate-y-0.5" />}
                    </div>
                    {friendlyRole(active.peer?.role, lang) && (
                      <div className="text-[10px] uppercase tracking-widest text-[#8a6f2e] font-semibold truncate">
                        {friendlyRole(active.peer?.role, lang)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => doStar(active)}
                    className="w-8 h-8 rounded-full hover:bg-black/[0.05] flex items-center justify-center"
                    aria-label="Star"
                    data-testid="active-star-btn"
                  >
                    <Star className={`w-4 h-4 ${active.is_starred ? "fill-[#c9a55c] text-[#c9a55c]" : "text-[#525252]"}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => doArchive(active)}
                    className="w-8 h-8 rounded-full hover:bg-black/[0.05] flex items-center justify-center"
                    aria-label="Archive"
                    data-testid="active-archive-btn"
                  >
                    <Archive className="w-4 h-4 text-[#525252]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => doDelete(active)}
                    className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center"
                    aria-label="Delete"
                    data-testid="active-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                  {active.vehicle_snapshot && (
                    <a href={`/vehicle/${active.vehicle_snapshot.id}`} className="ms-2 text-xs text-[#8a6f2e] hover:text-[#c9a55c] inline-flex items-center gap-2">
                      {active.vehicle_snapshot.image && (
                        <img src={active.vehicle_snapshot.image} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <span className="hidden sm:inline">
                        {active.vehicle_snapshot.year} {active.vehicle_snapshot.make} {active.vehicle_snapshot.model}
                        {active.vehicle_snapshot.price_sar && <> · {formatSAR(active.vehicle_snapshot.price_sar)} SAR</>}
                      </span>
                    </a>
                  )}
                </div>
              </header>

              <div ref={threadRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-2 min-h-0" data-testid="messages-thread">
                {messages.length === 0 ? (
                  <div className="text-center text-[#6b6b6b] py-12 text-sm">
                    {lang === "ar" ? "لا توجد رسائل. ابدأ المحادثة." : "No messages yet. Send the first one."}
                  </div>
                ) : messages.map((m) => (
                  <MemoBubble key={m.id} msg={m} mine={m.sender_id === user.id} />
                ))}
              </div>

              <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2" data-testid="message-form">
                <input
                  className="input-dark flex-1"
                  placeholder={lang === "ar" ? "اكتب رسالتك…" : "Type a message…"}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="message-input"
                />
                <button type="submit" className="btn-primary" disabled={sending || !body.trim()} data-testid="message-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
        </div>
      </div>
    </>
  );
}
